<script>
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import set from 'date-fns/set';
import Avatar from 'next/avatar/Avatar.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

import {
  GROUP_BY_FILTER,
  DATE_RANGE_OPTIONS,
  DATE_RANGE_ORDER,
} from '../constants';

export default {
  components: {
    WootDateRangePicker,
    Avatar,
    ToggleSwitch,
  },
  props: {
    currentFilter: {
      type: Object,
      default: () => null,
    },
    filterItemsList: {
      type: Array,
      default: () => [],
    },
    groupByFilterItemsList: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: 'agent',
    },
    selectedGroupByFilter: {
      type: Object,
      default: () => {},
    },
    showTimeFilter: {
      type: Boolean,
      default: false,
    },
    showIssueFilter: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'businessHoursToggle',
    'dateRangeChange',
    'filterChange',
    'groupByFilterChange',
    'issueFilterToggle',
  ],
  data() {
    return {
      currentSelectedFilter: this.currentFilter || null,
      currentDateRangeSelection: DATE_RANGE_OPTIONS.LAST_7_DAYS,
      customDateRange: [new Date(), new Date()],
      currentSelectedGroupByFilter: null,
      businessHoursSelected: false,
      startTime: '00:00',
      endTime: '23:59',
      excludeIssueTime: false,
    };
  },
  computed: {
    dateRange() {
      return DATE_RANGE_ORDER.map(id => DATE_RANGE_OPTIONS[id]).map(option => ({
        ...option,
        name: this.$t(option.translationKey),
      }));
    },
    isDateRangeSelected() {
      return (
        this.currentDateRangeSelection.id ===
        DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
      );
    },
    to() {
      if (this.isDateRangeSelected) {
        return this.toCustomDate(this.customDateRange[1]);
      }
      const endOffset =
        this.currentDateRangeSelection.endOffset ??
        this.currentDateRangeSelection.offset ??
        0;
      const toDate = subDays(new Date(), endOffset);
      return this.toCustomDate(toDate);
    },
    from() {
      if (this.isDateRangeSelected) {
        return this.fromCustomDate(this.customDateRange[0]);
      }
      const startOffset =
        this.currentDateRangeSelection.startOffset ??
        this.currentDateRangeSelection.offset ??
        0;
      const fromDate = subDays(new Date(), startOffset);
      return this.fromCustomDate(fromDate);
    },
    multiselectLabel() {
      const typeLabels = {
        agent: this.$t('AGENT_REPORTS.FILTER_DROPDOWN_LABEL'),
        label: this.$t('LABEL_REPORTS.FILTER_DROPDOWN_LABEL'),
        inbox: this.$t('INBOX_REPORTS.FILTER_DROPDOWN_LABEL'),
        team: this.$t('TEAM_REPORTS.FILTER_DROPDOWN_LABEL'),
        vip: this.$t('VIP_REPORTS.FILTER_DROPDOWN_LABEL'),
        vip_agent: this.$t('VIP_AGENT_REPORTS.FILTER_DROPDOWN_LABEL'),
      };
      return typeLabels[this.type] || this.$t('FORMS.MULTISELECT.SELECT_ONE');
    },
    groupBy() {
      if (this.isDateRangeSelected) {
        return GROUP_BY_FILTER[4].period;
      }
      return (
        this.currentDateRangeSelection.defaultGroupByPeriod ||
        GROUP_BY_FILTER[1].period
      );
    },
    showGroupByDropdown() {
      const options = this.currentDateRangeSelection.groupByOptions || [];
      return options.length > 1;
    },
  },
  watch: {
    filterItemsList(val) {
      this.currentSelectedFilter = !this.currentFilter
        ? val[0]
        : this.currentFilter;
      this.changeFilterSelection();
    },
    groupByFilterItemsList() {
      this.currentSelectedGroupByFilter = this.selectedGroupByFilter;
    },
    startTime() {
      if (this.showTimeFilter) {
        this.onDateRangeChange();
      }
    },
    endTime() {
      if (this.showTimeFilter) {
        this.onDateRangeChange();
      }
    },
  },
  mounted() {
    const defaultRange =
      this.dateRange.find(
        option => option.id === DATE_RANGE_OPTIONS.LAST_7_DAYS.id
      ) || this.dateRange[0];
    if (defaultRange) {
      this.currentDateRangeSelection = defaultRange;
    }
    this.onDateRangeChange();
  },
  methods: {
    onDateRangeChange() {
      this.$emit('dateRangeChange', {
        from: this.from,
        to: this.to,
        groupBy: this.groupBy,
        excludeIssueTime: this.excludeIssueTime,
      });
    },
    onBusinessHoursToggle() {
      this.$emit('businessHoursToggle', this.businessHoursSelected);
    },
    fromCustomDate(date) {
      if (!this.showTimeFilter) {
        return getUnixTime(startOfDay(date));
      }
      return this.combineDateAndTime(date, this.startTime, {
        isEnd: false,
        fallback: getUnixTime(startOfDay(date)),
      });
    },
    toCustomDate(date) {
      if (!this.showTimeFilter) {
        return getUnixTime(endOfDay(date));
      }
      return this.combineDateAndTime(date, this.endTime, {
        isEnd: true,
        fallback: getUnixTime(endOfDay(date)),
      });
    },
    onIssueFilterToggle(event) {
      this.excludeIssueTime = event.target.checked;
      this.onDateRangeChange();
      this.$emit('issueFilterToggle', this.excludeIssueTime);
    },
    combineDateAndTime(date, timeValue, options = {}) {
      const { isEnd, fallback } = options;
      if (!timeValue) {
        return fallback;
      }

      const [hours, minutes] = timeValue.split(':').map(value => Number(value));
      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return fallback;
      }

      const targetDate = set(new Date(date), {
        hours,
        minutes,
        seconds: isEnd ? 59 : 0,
        milliseconds: isEnd ? 999 : 0,
      });

      return getUnixTime(targetDate);
    },
    changeDateSelection(selectedRange) {
      this.currentDateRangeSelection = selectedRange;
      this.onDateRangeChange();
    },
    changeFilterSelection() {
      this.$emit('filterChange', this.currentSelectedFilter);
    },
    onChange(value) {
      this.customDateRange = value;
      this.onDateRangeChange();
    },
    changeGroupByFilterSelection() {
      this.$emit('groupByFilterChange', this.currentSelectedGroupByFilter);
    },
  },
};
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-3 gap-y-0.5 gap-x-2">
    <div v-if="type === 'agent'" class="multiselect-wrap--small">
      <p class="mb-2 text-xs font-medium">
        {{ $t('AGENT_REPORTS.FILTER_DROPDOWN_LABEL') }}
      </p>
      <multiselect
        v-model="currentSelectedFilter"
        :placeholder="multiselectLabel"
        label="name"
        track-by="id"
        :options="filterItemsList"
        :option-height="24"
        :show-labels="false"
        @update:model-value="changeFilterSelection"
      >
        <template #singleLabel="props">
          <div class="flex min-w-0 items-center gap-2">
            <Avatar
              :src="props.option.thumbnail"
              :status="props.option.availability_status"
              :name="props.option.name"
              :size="22"
              hide-offline-status
              rounded-full
            />
            <span class="my-0 text-n-slate-12 truncate">{{
              props.option.name
            }}</span>
          </div>
        </template>
        <template #options="props">
          <div class="flex items-center gap-2">
            <Avatar
              :src="props.option.thumbnail"
              :status="props.option.availability_status"
              :name="props.option.name"
              :size="22"
              hide-offline-status
              rounded-full
            />
            <p class="my-0 text-n-slate-12">
              {{ props.option.name }}
            </p>
          </div>
        </template>
      </multiselect>
    </div>

    <div v-else-if="type === 'label'" class="multiselect-wrap--small">
      <p class="mb-2 text-xs font-medium">
        {{ $t('LABEL_REPORTS.FILTER_DROPDOWN_LABEL') }}
      </p>
      <multiselect
        v-model="currentSelectedFilter"
        :placeholder="multiselectLabel"
        label="title"
        track-by="id"
        :options="filterItemsList"
        :option-height="24"
        :show-labels="false"
        @update:model-value="changeFilterSelection"
      >
        <template #singleLabel="props">
          <div class="flex items-center min-w-0 gap-2">
            <div
              :style="{ backgroundColor: props.option.color }"
              class="w-5 h-5 rounded-full"
            />

            <span class="my-0 text-n-slate-12 truncate">
              {{ props.option.title }}
            </span>
          </div>
        </template>
        <template #option="props">
          <div class="flex items-center min-w-0 gap-2">
            <div
              :style="{ backgroundColor: props.option.color }"
              class="flex-shrink-0 w-5 h-5 border border-solid rounded-full border-n-weak"
            />

            <span class="my-0 text-n-slate-12 truncate">
              {{ props.option.title }}
            </span>
          </div>
        </template>
      </multiselect>
    </div>

    <div v-else class="multiselect-wrap--small">
      <p class="mb-2 text-xs font-medium">
        <template v-if="type === 'inbox'">
          {{ $t('INBOX_REPORTS.FILTER_DROPDOWN_LABEL') }}
        </template>
        <template v-else-if="type === 'team'">
          {{ $t('TEAM_REPORTS.FILTER_DROPDOWN_LABEL') }}
        </template>
        <template v-else-if="type === 'vip'">
          {{ $t('VIP_REPORTS.FILTER_DROPDOWN_LABEL') }}
        </template>
        <template v-else>
          {{ $t('FORMS.MULTISELECT.SELECT_ONE') }}
        </template>
      </p>
      <multiselect
        v-model="currentSelectedFilter"
        track-by="id"
        label="name"
        :placeholder="multiselectLabel"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :options="filterItemsList"
        :searchable="false"
        :allow-empty="false"
        @update:model-value="changeFilterSelection"
      />
    </div>

    <div class="multiselect-wrap--small">
      <p class="mb-2 text-xs font-medium">
        {{ $t('REPORT.DURATION_FILTER_LABEL') }}
      </p>
      <multiselect
        v-model="currentDateRangeSelection"
        track-by="id"
        label="name"
        :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :options="dateRange"
        :searchable="false"
        :allow-empty="false"
        @select="changeDateSelection"
      />
    </div>

    <div
      class="flex items-center h-10 self-center order-5 md:order-2 md:justify-self-end"
    >
      <span class="mr-2 text-sm whitespace-nowrap">
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </span>
      <span>
        <ToggleSwitch
          v-model="businessHoursSelected"
          @change="onBusinessHoursToggle"
        />
      </span>
    </div>

    <div
      v-if="showIssueFilter"
      class="flex items-center h-10 self-center order-6 md:order-2 md:justify-self-end gap-2"
    >
      <input
        id="exclude-issue-time"
        :checked="excludeIssueTime"
        type="checkbox"
        class="h-4 w-4 rounded border-n-weak text-primary-500 focus:ring-primary-500"
        @change="onIssueFilterToggle"
      />
      <label
        for="exclude-issue-time"
        class="text-sm font-medium text-n-slate-12 select-none"
      >
        {{ $t('SUMMARY_REPORTS.EXCLUDE_ISSUE_TIME') }}
      </label>
    </div>

    <div v-if="showTimeFilter" class="order-3 md:order-3 col-span-full">
      <div class="flex flex-wrap gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('REPORT.START_TIME_LABEL') }}
          </span>
          <input
            v-model="startTime"
            type="time"
            class="w-[7rem] rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('REPORT.END_TIME_LABEL') }}
          </span>
          <input
            v-model="endTime"
            type="time"
            class="w-[7rem] rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
    </div>

    <div v-if="isDateRangeSelected" class="order-4 md:order-4">
      <p class="mb-2 text-xs font-medium">
        {{ $t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER') }}
      </p>
      <WootDateRangePicker
        show-range
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        class="auto-width"
        @change="onChange"
      />
    </div>

    <div
      v-if="showGroupByDropdown"
      class="multiselect-wrap--small order-5 md:order-5"
    >
      <p class="mb-2 text-xs font-medium">
        {{ $t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL') }}
      </p>
      <multiselect
        v-model="currentSelectedGroupByFilter"
        track-by="id"
        label="groupBy"
        :placeholder="$t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')"
        :options="groupByFilterItemsList"
        :allow-empty="false"
        :show-labels="false"
        @update:model-value="changeGroupByFilterSelection"
      />
    </div>
  </div>
</template>
